import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Card, Button, ListGroup, Spinner, Badge } from 'react-bootstrap';
import { getWhatsappAllowlist, removeWhatsappCustomer } from '../../actions/whatsappAccess';
import { LoadingState, ErrorBanner, EmptyState } from './StateViews';

const AllowlistPanel = ({ refreshKey, onRemoved, getWhatsappAllowlist, removeWhatsappCustomer }) => {
    const [rows, setRows] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [removing, setRemoving] = useState(null);

    const load = useCallback(async () => {
        setLoadFailed(false);
        const data = await getWhatsappAllowlist();
        if (!data) return setLoadFailed(true);
        setRows(data);
    }, [getWhatsappAllowlist]);

    useEffect(() => {
        load();
    }, [load, refreshKey]);

    const remove = async (phone) => {
        setRemoving(phone);
        const result = await removeWhatsappCustomer(phone);
        setRemoving(null);
        if (!result) return;
        setRows((current) => current.filter((row) => row.phone !== phone));
        onRemoved(phone);
    };

    return (
        <Card className="shadow-sm h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Allowlist</span>
                {rows && <Badge bg="secondary">{rows.length}</Badge>}
            </Card.Header>
            <Card.Body>
                <div className="text-muted small mb-3">
                    Only these customers receive WhatsApp flows and get replies from the menu. Everyone else is ignored. To add someone, pick them above and
                    switch on Allow WhatsApp flows.
                </div>
                {loadFailed && <ErrorBanner message="Failed to load the allowlist" onRetry={load} />}
                {!loadFailed && !rows && <LoadingState />}
                {rows && rows.length === 0 && <EmptyState className="mb-0">Nobody is allowlisted, so no customer receives WhatsApp flows.</EmptyState>}
            </Card.Body>
            {rows && rows.length > 0 && (
                <ListGroup variant="flush">
                    {rows.map((row) => (
                        <ListGroup.Item key={row.phone} className="d-flex justify-content-between align-items-center">
                            <span>
                                <span className="fw-semibold">{row.name || 'Unnamed'}</span>
                                <span className="text-muted ms-2">{row.phone}</span>
                            </span>
                            <Button size="sm" variant="outline-danger" disabled={removing === row.phone} onClick={() => remove(row.phone)}>
                                {removing === row.phone ? <Spinner size="sm" animation="border" /> : 'Remove'}
                            </Button>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </Card>
    );
};

AllowlistPanel.propTypes = {
    refreshKey: PropTypes.number.isRequired,
    onRemoved: PropTypes.func.isRequired,
    getWhatsappAllowlist: PropTypes.func.isRequired,
    removeWhatsappCustomer: PropTypes.func.isRequired
};

export default connect(null, { getWhatsappAllowlist, removeWhatsappCustomer })(AllowlistPanel);
